import { useState, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/axios';
import Badge from '../components/ui/Badge';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

const PRIORITY_MAP = {
  high: { bg: '#FEE2E2', color: '#EF4444', label: 'Hoch', sort: 0 },
  medium: { bg: '#FEF3C7', color: '#F59E0B', label: 'Mittel', sort: 1 },
  low: { bg: '#D1FAE5', color: '#10B981', label: 'Gering', sort: 2 },
};
const EFFORT_MAP = { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' };
const DISCIPLINE_BADGE = {
  cro: { bg: '#E6F4F2', color: '#0C9488' },
  sea: { bg: '#DBEAFE', color: '#3B82F6' },
  seo: { bg: '#EDE9FE', color: '#8B5CF6' },
  ux: { bg: '#FEF3C7', color: '#F59E0B' },
};
const CATEGORY_MAP = {
  copy: { label: 'Copy', bg: '#E0F2FE', color: '#0369A1' },
  cta: { label: 'CTA', bg: '#FEF3C7', color: '#B45309' },
  layout: { label: 'Layout', bg: '#EDE9FE', color: '#6D28D9' },
  psychology: { label: 'Psychologie', bg: '#FCE7F3', color: '#BE185D' },
  trust: { label: 'Trust', bg: '#DCFCE7', color: '#15803D' },
  performance: { label: 'Performance', bg: '#FFE4E6', color: '#BE123C' },
  mobile: { label: 'Mobile', bg: '#E0E7FF', color: '#4338CA' },
  analytics: { label: 'Analytics', bg: '#F1F5F9', color: '#475569' },
  accessibility: { label: 'A11y', bg: '#FEF2F2', color: '#9F1239' },
  seo: { label: 'SEO', bg: '#FFFBEB', color: '#A16207' },
  form: { label: 'Formular', bg: '#ECFEFF', color: '#0E7490' },
  checkout: { label: 'Checkout', bg: '#F0FDF4', color: '#166534' },
  personalization: { label: 'Personalisierung', bg: '#FAE8FF', color: '#86198F' },
  pricing: { label: 'Pricing', bg: '#FEF9C3', color: '#854D0E' },
  search: { label: 'Suche', bg: '#E0F2FE', color: '#075985' },
  internationalization: { label: 'i18n', bg: '#F3F4F6', color: '#374151' },
  'exit-intent': { label: 'Exit-Intent', bg: '#FFF1F2', color: '#9F1239' },
  other: { label: 'Sonstiges', bg: '#F3F4F6', color: '#4B5563' },
};

export default function CroMeasures() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['cro-measures-aggregate'],
    queryFn: async () => {
      const { data } = await api.get('/cro/measures');
      return data;
    },
  });

  const [filterPriority, setFilterPriority] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterUrl, setFilterUrl] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [sortBy, setSortBy] = useState('priority');

  const measures = data?.measures || [];
  const stats = data?.stats || {};

  const urls = useMemo(
    () => Array.from(new Set(measures.map(m => m.source.url))).sort(),
    [measures]
  );

  const filtered = useMemo(() => {
    let list = measures;
    if (filterPriority) list = list.filter(m => m.priority === filterPriority);
    if (filterDiscipline) list = list.filter(m => m.source.discipline === filterDiscipline);
    if (filterUrl) list = list.filter(m => m.source.url === filterUrl);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(m =>
        (m.measure || '').toLowerCase().includes(s) ||
        (m.goal_effect || '').toLowerCase().includes(s) ||
        (m.details || '').toLowerCase().includes(s)
      );
    }
    if (sortBy === 'priority') {
      list = [...list].sort((a, b) => (PRIORITY_MAP[a.priority]?.sort ?? 99) - (PRIORITY_MAP[b.priority]?.sort ?? 99));
    } else if (sortBy === 'date') {
      list = [...list].sort((a, b) => new Date(b.source.created_at) - new Date(a.source.created_at));
    } else if (sortBy === 'url') {
      list = [...list].sort((a, b) => a.source.url.localeCompare(b.source.url));
    }
    return list;
  }, [measures, filterPriority, filterDiscipline, filterUrl, search, sortBy]);

  return (
    <div>
      <div className="mb-5">
        <ProjectContextBadge />
        <h1 className="text-[23px] font-bold mb-0.5">CRO-Maßnahmen</h1>
        <p className="text-gray-500 text-[13.5px]">
          Alle Maßnahmen aus allen Analysen dieses Projekts — gefiltert, sortiert und durchsuchbar.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Gesamt', value: stats.total ?? '—', color: '#0C9488' },
          { label: 'Aus Analysen', value: stats.analyses ?? '—', color: '#3B82F6' },
          { label: 'Hoch', value: stats.high ?? '—', color: '#EF4444' },
          { label: 'Mittel', value: stats.medium ?? '—', color: '#F59E0B' },
          { label: 'Gering', value: stats.low ?? '—', color: '#10B981' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4 text-center">
            <div className="text-[26px] font-extrabold" style={{ color: kpi.color }}>{kpi.value}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Filter-Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-3 mb-4 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Suchen in Maßnahme/Effekt/Details…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-[12.5px] outline-none focus:border-teal flex-1 min-w-[200px]"
        />

        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-[12.5px] outline-none focus:border-teal bg-white"
        >
          <option value="">Alle Prioritäten</option>
          <option value="high">Nur Hoch</option>
          <option value="medium">Nur Mittel</option>
          <option value="low">Nur Gering</option>
        </select>

        <select
          value={filterDiscipline}
          onChange={(e) => setFilterDiscipline(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-[12.5px] outline-none focus:border-teal bg-white"
        >
          <option value="">Alle Disziplinen</option>
          <option value="cro">CRO</option>
          <option value="sea">SEA</option>
          <option value="seo">SEO</option>
          <option value="ux">UX</option>
        </select>

        <select
          value={filterUrl}
          onChange={(e) => setFilterUrl(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-[12.5px] outline-none focus:border-teal bg-white max-w-[280px] truncate"
        >
          <option value="">Alle URLs</option>
          {urls.map(u => <option key={u} value={u}>{u}</option>)}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-[12.5px] outline-none focus:border-teal bg-white"
        >
          <option value="priority">Sortiert: Priorität</option>
          <option value="date">Sortiert: Datum</option>
          <option value="url">Sortiert: URL</option>
        </select>

        <span className="text-[11.5px] text-gray-500 ml-auto">{filtered.length} von {measures.length}</span>
      </div>

      {/* Tabelle */}
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium">Maßnahme</th>
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium">Quelle</th>
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium w-20">Disziplin</th>
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium w-24">Priorität</th>
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium w-24">Aufwand</th>
              <th className="px-5 py-3 text-[12px] text-gray-500 font-medium w-20">Impact</th>
              <th className="px-5 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const pr = PRIORITY_MAP[m.priority] || PRIORITY_MAP.medium;
              const dBadge = DISCIPLINE_BADGE[m.source.discipline] || DISCIPLINE_BADGE.cro;
              const isOpen = openId === m.id;
              return (
                <Fragment key={m.id}>
                  <tr
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => setOpenId(isOpen ? null : m.id)}
                  >
                    <td className="px-5 py-3 align-top">
                      <div className="font-medium">{m.measure}</div>
                      {(m.category || m.element_ref) && (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {m.category && (() => {
                            const c = CATEGORY_MAP[m.category] || CATEGORY_MAP.other;
                            return (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium uppercase tracking-wide"
                                style={{ background: c.bg, color: c.color }}
                              >
                                {c.label}
                              </span>
                            );
                          })()}
                          {m.element_ref && m.element_ref !== 'übergeordnet' && (
                            <span className="text-[11px] text-gray-500">→ {m.element_ref}</span>
                          )}
                          {m.element_ref === 'übergeordnet' && (
                            <span className="text-[11px] text-gray-400 italic">übergeordnet</span>
                          )}
                        </div>
                      )}
                      {m.goal_effect && (
                        <div className="text-[12px] text-gray-500 mt-1 line-clamp-2">{m.goal_effect}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top max-w-[260px]">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/cro/analyses/${m.source.analysis_id}`); }}
                        className="text-[12px] text-teal hover:underline truncate block max-w-full"
                        title={m.source.url}
                      >
                        {m.source.url}
                      </button>
                      <div className="text-[10.5px] text-gray-400">{m.source.page_type} · Analyse #{m.source.analysis_id}</div>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <Badge bg={dBadge.bg} color={dBadge.color}>{(m.source.discipline || 'cro').toUpperCase()}</Badge>
                    </td>
                    <td className="px-5 py-3 align-top"><Badge bg={pr.bg} color={pr.color}>{pr.label}</Badge></td>
                    <td className="px-5 py-3 align-top text-[12px]">{EFFORT_MAP[m.effort] || m.effort || '—'}</td>
                    <td className="px-5 py-3 align-top font-medium text-teal">{m.conv_impact || '—'}</td>
                    <td className="px-5 py-3 align-top text-gray-400 text-center">{m.details ? (isOpen ? '−' : '+') : ''}</td>
                  </tr>
                  {isOpen && m.details && (
                    <tr className="bg-gray-50/70 border-b border-gray-100">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-2">Tiefe Begründung & Test-Plan</div>
                        {m.details.split(/\n\n+/).map((para, i) => (
                          <p key={i} className="text-[13px] text-gray-700 leading-relaxed mb-2 last:mb-0">{para}</p>
                        ))}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">
            {measures.length === 0
              ? 'Noch keine Maßnahmen — sobald Analysen abgeschlossen sind, erscheinen sie hier.'
              : 'Keine Maßnahmen passen zu diesen Filtern.'}
          </div>
        )}
        {isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>
        )}
      </div>
    </div>
  );
}
